import { styled, css, Container } from "@mui/material";

import { getColor } from "@hooks/useColors";
import { cssPropertiesToString } from "@utils/cssPropertiesToString";

const marginBlockEndSmall = "8px";
const marginBlockEndMedium = "16px";
const marginBlockEndBig = "24px";

const extraVerticalPaddingSmall = "8px";
const extraVerticalPaddingMedium = "16px";
const extraVerticalPaddingBig = "24px";

export const ArticleContent = styled("div")(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      padding-left: 0px;
      padding-right: 0px;
    }

    ${theme.breakpoints.up("tablet")} {
      padding-left: 64px;
      padding-right: 64px;
    }

    ${theme.breakpoints.up("desktop")} {
      padding-left: 0px;
      padding-right: 0px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      padding-left: 110px;
      padding-right: 110px;
    }

    h2 {
      background: ${theme.portfolio?.subheader?.gradient ??
      theme.mainContentGradient};
      background-clip: text;
      color: transparent;
      text-indent: -24px;
      padding-left: 24px;
      width: fit-content;

      ::before {
        content: "< ";
      }

      ::after {
        content: " >";
        white-space: nowrap;
      }

      ${theme.breakpoints.up("mobile")} {
        margin-block-end: ${marginBlockEndMedium};
        ${cssPropertiesToString(theme.typography.heading_h2_medium_mobile)}
      }

      ${theme.breakpoints.up("tablet")} {
        ${cssPropertiesToString(theme.typography.heading_h2_medium_tablet)}
      }

      ${theme.breakpoints.up("desktop")} {
        margin-block-end: ${marginBlockEndBig};
        ${cssPropertiesToString(theme.typography.heading_h2_medium_desktop)}
      }
    }

    > p {
      ${cssPropertiesToString(theme.typography.paragraph_large_regular)}
      position: relative;

      ${theme.breakpoints.up("mobile")} {
        margin-block-end: ${marginBlockEndMedium};
      }

      ${theme.breakpoints.up("desktop")} {
        margin-block-end: ${marginBlockEndBig};
      }
    }

    p {
      text-align: justify;
    }

    ol,
    ul {
      text-align: left;
    }

    p:has(+ p),
    p:has(+ ul),
    p:has(+ ol) {
      margin-block-end: ${marginBlockEndSmall};
    }

    > h3 {
      ${cssPropertiesToString(theme.typography.paragraph_xxlarge_semibold)}
      margin-block-end: ${marginBlockEndSmall};
    }

    > div {
      ${theme.breakpoints.up("mobile")} {
        margin-block-end: ${marginBlockEndMedium};
        padding: ${extraVerticalPaddingSmall} 0;
      }

      ${theme.breakpoints.up("tablet")} {
        padding: ${extraVerticalPaddingMedium} 0;
      }

      ${theme.breakpoints.up("desktop")} {
        margin-block-end: ${marginBlockEndBig};
        padding: ${extraVerticalPaddingBig} 0;
      }
    }

    ul,
    ol {
      ${cssPropertiesToString(theme.typography.paragraph_large_regular)}
      padding-left: 1.75em;

      ${theme.breakpoints.up("mobile")} {
        margin-block-end: ${marginBlockEndMedium};
      }

      ${theme.breakpoints.up("desktop")} {
        margin-block-end: ${marginBlockEndBig};
      }
    }

    ul {
      list-style-type: disc;
    }

    ul ul {
      list-style-type: circle;
      margin-block-end: 0;
      margin-block-start: ${marginBlockEndSmall};
    }

    ul ul ul {
      list-style-type: square;
    }

    ol {
      list-style-type: decimal;
    }

    ol ol {
      list-style-type: lower-alpha;
      margin-block-end: 0;
      margin-block-start: ${marginBlockEndSmall};
    }

    li {
      margin-block-end: ${marginBlockEndSmall};
      ${cssPropertiesToString(theme.typography.paragraph_large_regular)}
      text-align: left;
    }

    li > ul,
    li > ol {
      padding-left: 1.5em;
    }

    img {
      width: min(100%, 800px);
      height: auto;
      display: block;
      overflow: hidden;
      margin: 0 auto;

      ${theme.breakpoints.up("mobile")} {
        padding: ${extraVerticalPaddingSmall} 0;
      }

      ${theme.breakpoints.up("tablet")} {
        padding: ${extraVerticalPaddingMedium} 0;
      }

      ${theme.breakpoints.up("desktop")} {
        padding: ${extraVerticalPaddingBig} 0;
      }
    }

    a {
      ${cssPropertiesToString(theme.typography.paragraph_large_semibold)}
      text-decoration: underline;
    }

    strong {
      ${cssPropertiesToString(theme.typography.paragraph_large_semibold)}
    }

    table {
      width: 100%;
      border-collapse: collapse;

      ${theme.breakpoints.up("mobile")} {
        margin-block-end: ${marginBlockEndMedium};
        font-size: 14px;
      }

      ${theme.breakpoints.up("desktop")} {
        margin-block-end: ${marginBlockEndBig};
        ${cssPropertiesToString(theme.typography.paragraph_large_regular)}
      }

      border-radius: 24px;
      overflow: hidden;
    }

    th,
    td {
      padding: 10px 16px;
      text-align: left;
      vertical-align: top;
    }

    th {
      ${cssPropertiesToString(theme.typography.paragraph_large_medium)}
      background: ${getColor(c => c.tokens.stroke["sub-300"], theme)};
    }

    tr:nth-of-type(even) td {
      background: ${getColor(c => c.tokens.bg["soft-200"], theme)};
    }
  `,
);

export const ArticleContainer = styled(Container)(
  ({ theme }) => css`
    ${theme.breakpoints.up("mobile")} {
      padding-left: 16px;
      padding-right: 16px;
    }

    ${theme.breakpoints.up("tablet")} {
      padding-left: 64px;
      padding-right: 64px;
    }

    ${theme.breakpoints.up("desktop")} {
      padding-left: 112px;
      padding-right: 112px;
    }

    ${theme.breakpoints.up("desktopXl")} {
      padding-left: 220px;
      padding-right: 220px;
    }
  `,
);
